import { FC } from "react";
import { Theme } from "apps/website/maps/Theme.map";
import ProductCard, {
  IProductCard,
} from "apps/website/components/feature/Card/ProductCard/ProductCard";
import Text from "apps/website/components/base/Text/Text";
import {
  Display,
  legacySizeCollectionMap,
} from "apps/website/components/base/Text/Text.map";
import Column from "apps/website/components/layout/Column/Column";
import Container from "apps/website/components/layout/Container/Container";
import Grid from "apps/website/components/layout/Grid/Grid";
import Section from "apps/website/components/layout/Section/Section";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import { ILink } from "apps/website/components/base/Button/Button.map";
import Button from "apps/website/components/base/Button/Button";

import {
  BreakpointOffset,
  BreakpointSpan,
  Offset,
} from "../../layout/Column/Column.map";
import { CardCtaAction } from "../../feature/Card/Card";
import { getSectionSize } from "../../layout/Section/Section.utils";

const fullWidth = 12;
const halfWidth = 6;
const quarterWidth = 3;
const thirdWidth = 4;

const rowWithThreeItems = 3;
const rowWithFourItems = 4;

const getColumnSpans = (ctaAction?: CardCtaAction, noOfItems: number = rowWithFourItems): BreakpointSpan => {
  const isLinkAction = ctaAction === "link";
  const shouldBeFullWidth = noOfItems === rowWithThreeItems || isLinkAction;
  const mobileSpan = shouldBeFullWidth ? fullWidth : halfWidth;
  const lgSpan = noOfItems === rowWithFourItems ? quarterWidth : thirdWidth;

  return { default: mobileSpan, lg: lgSpan };
};

const getCenteredOffset = (span: number): Offset => Math.floor((fullWidth - span) / 2) + 1 as Offset;

export interface IProductList {
  title?: string;
  subtitle?: string;
  subtitleDisplay?: Display;
  theme?: Theme;
  items: IProductCard[];
  ctaAction?: CardCtaAction;
  cta?: ILink;
  noOfItems?: number;
}

const SectionProductList: FC<IProductList> = (
  { title, subtitle, subtitleDisplay = "subtitle", theme = "default", items, cta, ctaAction, noOfItems },
) => {

  const columnSpans = getColumnSpans(ctaAction, noOfItems);
  const singleItemOffset: BreakpointOffset = {
    default: getCenteredOffset(columnSpans.default!),
    lg: getCenteredOffset(columnSpans.lg!),
  };
  const offsetMap: Record<number, Offset> = {
    2: 4,
  };

  const getOffset = (index: number): BreakpointOffset | undefined => {
    if (items.length === 1) {
      return singleItemOffset;
    }

    return { lg: index === 0 ? offsetMap[items.length] : undefined };
  };

  return (
    <Section component="SectionProductList" theme={ theme } size={ getSectionSize(theme) }>
      <Container>
        { title && (
          <>
            <Text tag="h2" display="title" size={ legacySizeCollectionMap.titleLg } align="center">
              { title }
            </Text>
            <Spacer size="xl" />
          </>
        ) }
        { subtitle && (
          <Grid>
            <Column spans={ { lg: 10 } } offset={ { lg: 2 } }>
              <Text
                display={ subtitleDisplay }
                size={ subtitleDisplay === "subtitle" ?
                  legacySizeCollectionMap.titleMd :
                  { default: "default", lg: "md" }
                }
                align="center"
                whitespace={ { lg: "preWrap" } }
              >{ subtitle }</Text>
              <Spacer size="xl" />
            </Column>
          </Grid>
        ) }
        <Grid>
          <>
            { items.map((product, index) => (
              <Column
                key={ product.title } spans={ columnSpans }
                offset={ getOffset(index) }
              >
                <ProductCard { ...product } />
              </Column>
            )) }
            { cta && (
              <Column align="center">
                <Spacer size="xl" />
                <Button
                  to={ cta.to }
                  design={ cta.design }
                  color={ cta.color }
                  data-testid={ cta.testId }
                >{ cta.name }</Button>
              </Column>
            ) }
          </>
        </Grid>
      </Container>
    </Section>
  );
};

export default SectionProductList;
